import { Flash, Button, Link, DropdownMenu, Text } from '@acast/decibel';
import {
  SyncAltRegularGlyph,
  TrashAltRegularGlyph,
  EllipsisHRegularGlyph,
} from '@acast/decibel-glyphs';
import T from 'prop-types';
import React from 'react';
import spotifyLogo from 'server/services/web/public/images/distribution-logo-spotify.svg';
import { presentModal } from 'services/modals';
import Modal from 'shared/components/modal';
import Distribution from './distribution';

const spotifyDistributionCopy = () => (
  <>
    <Text variant="body2">
      To access Spotify-specific data, you can request access to a Spotify for Podcasters dashboard
      for your podcast.
    </Text>
    <Text variant="body2">
      1. You must first have an active Spotify account. To sign up, go to{' '}
      <Link href="https://www.spotify.com/signup/" target="_blank" rel="noopener noreferrer">
        https://www.spotify.com/signup/
      </Link>
      .
    </Text>
    <Text variant="body2">
      2. Also ensure the e-mail address you’ve provided under the podcast’s General Settings is
      correct. Spotify will use this e-mail address to confirm you are the owner of your RSS feed.
      In Create, go to Settings, then scroll down to RSS Settings to find or change your e-mail
      address.
    </Text>
    <Text variant="body2">
      3. Sign up to Spotify for Podcasters at{' '}
      <Link href="https://podcasters.spotify.com/" target="_blank" rel="noopener noreferrer">
        https://podcasters.spotify.com/
      </Link>
      . You will be sent a verification code by e-mail to confirm your ownership of the show.
    </Text>
    <Text variant="body2">
      Once you have access to your Spotify dashboard, you can access further detail about your
      Spotify listens, including followers, listen-through rates and demographic data.
    </Text>
  </>
);

const renderDescription = (enabled, data) => {
  if (enabled) {
    if (data.spotifyUri) {
      return (
        <>
          <Text variant="body2">
            Your Spotify URI is: <b>{data.spotifyUri}</b>
          </Text>
          {data.statusCode && data.statusDescription ? (
            <Text variant="body2">
              The current distribution status of your show is: <b>{data.statusDescription}</b>
            </Text>
          ) : (
            <Text variant="body2">
              Please note that you may not find your show at this URI immediately; it can take 24-48
              hrs to get approval from Spotify. Also, it is not possible to cancel or remove the
              submission before it has been approved by Spotify. Once your show is available in
              Spotify you can click ‘Remove’ if you wish to disconnect your show from Spotify.
            </Text>
          )}
          {spotifyDistributionCopy()}

          {data.validationErrors && data.validationErrors.length > 0 && (
            <Flash intent="danger">
              Valition errors: {data.validationErrors.map(error => error.message)}
            </Flash>
          )}
        </>
      );
    }
    return <Text variant="body2">Checking distribution status...</Text>;
  }
  return (
    <>
      <Text variant="body2">
        Clicking ‘Submit’ will submit your show to be added to Spotify’s podcast catalogue. Spotify
        can take 24-48 hrs to approve your feed.
      </Text>
      {spotifyDistributionCopy()}
    </>
  );
};

const onSubmit = handleSubmit => {
  presentModal(
    <Modal
      title="Submission for distribution of content on Spotify"
      dialog={
        <>
          <Text variant="body2">
            Prior to submitting content for distribution on Spotify, please confirm the following:
          </Text>
          <Text variant="body2">
            I am the owner of or rights holder to all the content submitted and I have the full
            authority to grant distribution of the same via Spotify.
          </Text>
          <Text variant="body2">
            Distribution of the content via Spotify shall not constitute any un-authorized use of
            the content including music.
          </Text>
          <Text variant="body2">
            If my content is accepted for inclusion and distribution on Spotify, I accept that
            Spotify distributes my content to listeners (free and premium users) on the Spotify app
            and/or website.
          </Text>
          <Text variant="body2" mb={0}>
            There is no guarantee that the content submitted herein will be placed on and
            distributed via Spotify. Spotify decides at its sole discretion whether to admit or
            decline content including the time period during which content is distributed via
            Spotify
          </Text>
        </>
      }
      actions={[
        { name: 'Cancel', variant: 'tertiary', value: false },
        { name: 'I Agree', variant: 'primary', value: true },
      ]}
    />
  ).then(shouldSubmit => shouldSubmit && handleSubmit());
};

const onRemove = handleRemove => {
  presentModal(
    <Modal
      title="Remove distribution on Spotify"
      dialog="Are you sure you want to remove your show from Spotify? Doing so will make it inaccessible for your listeners on the Spotify platform."
      actions={[
        { name: 'Cancel', value: false, variant: 'tertiary' },
        { name: 'Remove', value: true, variant: 'primary', intent: 'danger' },
      ]}
    />
  ).then(shouldRemove => shouldRemove && handleRemove());
};

const renderButton = (getIntegration, addIntegration, deleteIntegration, integrationStatus) =>
  integrationStatus.enabled ? (
    <DropdownMenu toggle={<Button glyph={EllipsisHRegularGlyph} title="More" size="small" />}>
      <DropdownMenu.Menu>
        <DropdownMenu.Item
          data-test-id="SpotifyDistribution__button-getIntegration"
          glyph={SyncAltRegularGlyph}
          onClick={() => getIntegration()}
        >
          Check status
        </DropdownMenu.Item>
        <DropdownMenu.Item
          data-test-id="SpotifyDistribution__button-removeIntegration"
          glyph={TrashAltRegularGlyph}
          onClick={() => onRemove(deleteIntegration)}
        >
          Remove show
        </DropdownMenu.Item>
      </DropdownMenu.Menu>
    </DropdownMenu>
  ) : (
    <Button
      variant="secondary"
      data-test-id="SpotifyDistribution__button"
      onClick={() => onSubmit(addIntegration)}
      size="small"
      busy={integrationStatus.pending}
    >
      Submit
    </Button>
  );

const SpotifyDistribution = ({
  getIntegration,
  addIntegration,
  deleteIntegration,
  integrationStatus,
}) => {
  return (
    <Distribution
      title="Spotify"
      logo={spotifyLogo}
      button={renderButton(getIntegration, addIntegration, deleteIntegration, integrationStatus)}
      logoAlt="Spotify Logo"
    >
      {renderDescription(integrationStatus.enabled, integrationStatus.data)}
    </Distribution>
  );
};

SpotifyDistribution.propTypes = {
  getIntegration: T.func.isRequired,
  addIntegration: T.func.isRequired,
  deleteIntegration: T.func.isRequired,
  integrationStatus: T.object.isRequired,
};

export default SpotifyDistribution;
